import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { EmailClient } from '@amazon-connect/email';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData } from '../../../utils/emailContactHelpers';
import { fetchEmailBody } from '../../../services/fetchEmailBody';
import { EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import { resolveEmailAttachments } from '../../../services/resolveEmailAttachments';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import DOMPurify from 'dompurify';
import postEvent from '../../../services/postEvent';
import { EMAIL_FOCUS_TIMESTAMP_KEY } from '../../../constants/localStorage';
import getCurrentTime from '../../../utils/timeFactory';
import { datadogLogs } from '@datadog/browser-logs';
import { useAppContext } from '../../../AppContext';

export function filterThreadContacts(contacts: ThreadContact[], activeContactId: string): ThreadContact[] {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  return contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const { updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread } = useEmailContext();
  const { agent } = useAppContext();
  const isInitializedRef = useRef(false);
  const emailClientRef = useRef<EmailClient | null>(null);
  const onErrorRef = useRef(onError);

  onErrorRef.current = onError;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const client = emailClientRef.current;

      if (!client) return;

      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);
        const contacts = await fetchEmailThread(contactAssociationId);

        const filteredContacts = filterThreadContacts(contacts, activeContactId);

        const messages: EmailMessage[] = await Promise.all(
          filteredContacts.map(async threadContact => {
            const emailData = await client.getEmailData({
              contactId: threadContact.contactId,
              activeContactId
            });

            const { bodyLocation, attachmentLocations, subject, from, to, cc, direction, deliveredTo } = emailData;

            let bodyHtml: string | undefined;

            if (bodyLocation) {
              const rawBodyHtml = await fetchEmailBody(bodyLocation, threadContact.contactId);
              if (rawBodyHtml) {
                const sanitizedHtml = DOMPurify.sanitize(rawBodyHtml);

                const htmlWasModified = sanitizedHtml !== rawBodyHtml;

                if (htmlWasModified) {
                  datadogLogs.logger.info('Email body sanitized', {
                    contactId: threadContact.contactId
                  });
                }
                bodyHtml = sanitizedHtml;
              }
            }

            const attachments = await resolveEmailAttachments(attachmentLocations, threadContact.contactId);

            return {
              contactId: threadContact.contactId,
              subject,
              from,
              // deliveredTo is the actual inbox address for inbound emails (e.g. the queue
              // address) and takes precedence over the generic `to` field from the SDK
              to: deliveredTo ? [deliveredTo] : (to ?? []),
              cc,
              bodyHtml,
              attachments,
              timestamp: new Date(threadContact.initiationTimestamp).getTime(),
              direction
            };
          })
        ).then(msgs => msgs.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)));

        updateEmailThread(activeContactId, { contactAssociationId, messages });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      const contactId = contact.getContactId();
      const initiationMethod = contact.getInitiationMethod()?.toUpperCase();

      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
        localStorage.setItem(EMAIL_FOCUS_TIMESTAMP_KEY, JSON.stringify(getCurrentTime()));
      });

      contact.onDestroy(() => {
        const previousTimestamp = localStorage.getItem(EMAIL_FOCUS_TIMESTAMP_KEY);

        if (previousTimestamp) {
          const direction = initiationMethod;

          void postEvent(
            contactId,
            'focus',
            { startedAt: new Date(JSON.parse(previousTimestamp) as string), endedAt: getCurrentTime() },
            'EMAIL',
            direction
          ).catch((error: Error) => {
            datadogLogs.logger.warn('Failed to post email focus event', { error, agent: agent?.getName() });
          });

          localStorage.removeItem(EMAIL_FOCUS_TIMESTAMP_KEY);
        }
      });
    };

    connect.agent(agent => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);

      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .filter(c => c.getInitiationMethod()?.toUpperCase() !== EMAIL_INITIATION_METHODS.AGENT_REPLY)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread]);
}
